import React, { createContext, useContext, useEffect, useRef } from 'react';

import type { VgpsLivekitWebRuntime } from '../core/runtime/types';

const VgpsRuntimeContext = createContext<VgpsLivekitWebRuntime | null>(null);

export interface VgpsLivekitProviderProps {
  runtime: VgpsLivekitWebRuntime;
  children: React.ReactNode;
  /**
   * When true, disconnect the shared signaling WebSocket while the tab is
   * hidden and reconnect it when the tab becomes visible again. Per-session
   * transports (relay WS + keepalive) are released independently by the
   * session hooks (`pauseWhenHidden`). `connectSignaling` is reentrant, and the
   * session hooks `await runtime.connectSignaling()` before opening a session
   * (openLiveSession requires a live WS — it does NOT auto-connect), so a
   * re-leased session on the visible transition works even if this handler and
   * the hooks fire in any order. Default false.
   */
  disconnectSignalingWhenHidden?: boolean;
}

export function VgpsLivekitProvider({
  runtime,
  children,
  disconnectSignalingWhenHidden = false,
}: VgpsLivekitProviderProps): JSX.Element {
  const runtimeRef = useRef<VgpsLivekitWebRuntime | null>(runtime);

  useEffect(() => {
    runtimeRef.current = runtime;
    if (!disconnectSignalingWhenHidden || typeof document === 'undefined') {
      return () => { if (runtimeRef.current === runtime) runtimeRef.current = null; };
    }
    const handle = (): void => {
      const currentRuntime = runtimeRef.current;
      if (!currentRuntime) return;
      if (document.visibilityState === 'hidden') {
        void currentRuntime.disconnectSignaling().catch(() => {});
      } else {
        void currentRuntime.connectSignaling().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handle);
    return () => {
      document.removeEventListener('visibilitychange', handle);
      if (runtimeRef.current === runtime) runtimeRef.current = null;
    };
  }, [runtime, disconnectSignalingWhenHidden]);

  return <VgpsRuntimeContext.Provider value={runtime}>{children}</VgpsRuntimeContext.Provider>;
}

export function useRuntimeContext(): VgpsLivekitWebRuntime | null {
  return useContext(VgpsRuntimeContext);
}
